import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useSEO, getServiceSchema, getBreadcrumbSchema } from '../../../utils/seo';

export default function TicaretSirketlerHukuku() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useSEO({
    title: 'Ticaret ve Şirketler Hukuku Isparta Avukat Danışmanlık',
    description: 'Isparta Yıldız & Başyiğit Hukuk Bürosu ticaret ve şirketler hukuku alanında şirket kuruluşu, birleşme ve devir işlemleri, ortaklık uyuşmazlıkları, ticari sözleşmeler ve ticari dava takibi konularında profesyonel hukuki danışmanlık hizmetleri sunmaktadır. Ticari faaliyetlerinizde güvenilir hukuki destek için yanınızdayız.',
    keywords: 'ticaret hukuku Isparta, şirket kuruluşu, ticari sözleşmeler, ortaklık uyuşmazlıkları, ticari dava',
    canonical: `${siteUrl}/services/ticaret-sirketler-hukuku`,
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        getServiceSchema({
          name: 'Ticaret ve Şirketler Hukuku',
          description: 'Şirket kuruluşu, birleşme, devir işlemleri ve ticari uyuşmazlıklar konusunda profesyonel hukuki danışmanlık',
          url: `${siteUrl}/services/ticaret-sirketler-hukuku`
        }),
        getBreadcrumbSchema([
          { name: 'Ana Sayfa', url: siteUrl },
          { name: 'Hizmetler', url: `${siteUrl}/services` },
          { name: 'Ticaret ve Şirketler Hukuku', url: `${siteUrl}/services/ticaret-sirketler-hukuku` }
        ])
      ]
    }
  });

  const serviceFeatures = [
    {
      title: 'Şirket Kuruluşu',
      description: 'Limited, anonim ve diğer şirket türlerinin kuruluş işlemleri',
      icon: 'ri-building-2-line'
    },
    {
      title: 'Ortaklık Anlaşmaları',
      description: 'Ortaklar arası anlaşmaların hazırlanması ve müzakeresi',
      icon: 'ri-handshake-line'
    },
    {
      title: 'Birleşme ve Devir',
      description: 'Şirket birleşmeleri, devirleri ve yeniden yapılandırma',
      icon: 'ri-shuffle-line'
    },
    {
      title: 'Ticari Sözleşmeler',
      description: 'Bayilik, distribütörlük ve ticari temsilcilik sözleşmeleri',
      icon: 'ri-file-text-line'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'İlk Görüşme',
      description: 'İhtiyaçlarınızı dinler ve en uygun çözümü belirleriz'
    },
    {
      step: '02',
      title: 'Hukuki Analiz',
      description: 'Durumunuzu detaylı analiz eder, riskleri değerlendiririz'
    },
    {
      step: '03',
      title: 'Strateji Geliştirme',
      description: 'Size özel hukuki strateji ve eylem planı oluştururuz'
    },
    {
      step: '04',
      title: 'Uygulama',
      description: 'Belirlenen stratejiye göre işlemleri gerçekleştiririz'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white py-20">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/f1c66be3c78e20b4771f04b1bfcbf757.jpg')`
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/70"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center mb-4 animate-fade-in-up">
              <Link to="/services" className="text-gray-300 hover:text-white transition-colors font-poppins font-light">
                Hizmetler
              </Link>
              <i className="ri-arrow-right-s-line mx-2 text-gray-400"></i>
              <span className="text-white font-poppins font-light">Ticaret ve Şirketler Hukuku</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fade-in-up font-poppins" style={{ animationDelay: '0.1s' }}>
              Ticaret ve Şirketler Hukuku
            </h1>
            
            <p className="text-xl text-gray-200 leading-relaxed animate-fade-in-up font-poppins font-light" style={{ animationDelay: '0.2s' }}>
              Şirket kuruluşundan birleşme işlemlerine kadar ticaret hukukunun tüm alanlarında 
              profesyonel danışmanlık ve hukuki temsil hizmetleri sunuyoruz.
            </p>
          </div>
        </div>
      </section>

      {/* Service Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 animate-fade-in-up font-poppins">
              Hizmet Alanlarımız
            </h2>
            <div className="w-16 h-0.5 bg-slate-900 mx-auto mb-6"></div>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto animate-fade-in-up font-poppins font-light" style={{ animationDelay: '0.1s' }}>
              Ticaret ve şirketler hukuku alanında kapsamlı hizmetler sunuyoruz
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {serviceFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-all duration-300 animate-fade-in-up hover-lift"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-16 h-16 bg-slate-900 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4 font-poppins">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed font-poppins font-light">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 animate-fade-in-up font-poppins">
              Çalışma Sürecimiz
            </h2>
            <div className="w-16 h-0.5 bg-slate-900 mx-auto mb-6"></div>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto animate-fade-in-up font-poppins font-light" style={{ animationDelay: '0.1s' }}>
              Sistematik yaklaşımımızla size en iyi hizmeti sunuyoruz
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div
                key={index}
                className="text-center animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-20 h-20 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-white font-poppins">{step.step}</span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4 font-poppins">
                  {step.title}
                </h3>
                <p className="text-slate-600 leading-relaxed font-poppins font-light">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 animate-fade-in-up font-poppins">
              Ticaret ve Şirketler Hukuku Hakkında
            </h2>
            
            <div className="space-y-6 text-slate-600 leading-relaxed font-poppins font-light">
              <p className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                Ticaret ve şirketler hukuku, işletmelerin kuruluşundan tasfiyesine kadar tüm süreçlerde 
                karşılaştıkları hukuki meseleleri düzenleyen önemli bir hukuk dalıdır. Bu alanda uzman 
                avukat kadromuzla, müvekkillerimize kapsamlı hukuki danışmanlık ve temsil hizmetleri sunuyoruz.
              </p>
              
              <p className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                Şirket kuruluşu sürecinde, en uygun şirket türünün seçilmesinden ana sözleşmenin 
                hazırlanmasına, tescil işlemlerinden vergi dairesi kayıtlarına kadar tüm aşamalarda 
                yanınızdayız. Limited şirket, anonim şirket, kolektif şirket ve komandit şirket 
                kuruluşlarında uzman desteği sağlıyoruz.
              </p>
              
              <p className="animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                Mevcut şirketlerin hukuki sorunlarının çözümünde, ortaklar arası anlaşmazlıkların 
                giderilmesinde, şirket birleşme ve devirlerinde, sermaye artırımı ve azaltımı 
                işlemlerinde profesyonel hizmet sunuyoruz. Ayrıca ticari sözleşmelerin hazırlanması, 
                müzakeresi ve uygulanması konularında da danışmanlık hizmeti veriyoruz.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4 animate-fade-in-up font-poppins">
            Ticaret Hukuku Danışmanlığı
          </h2>
          <p className="text-lg text-gray-200 mb-8 animate-fade-in-up font-poppins font-light" style={{ animationDelay: '0.1s' }}>
            Şirketinizin hukuki ihtiyaçları için uzman desteği alın
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <button className="bg-white text-slate-900 px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors font-poppins font-semibold whitespace-nowrap cursor-pointer">
              Randevu Al
            </button>
            <button className="border border-white text-white px-8 py-3 rounded-lg hover:bg-white hover:text-slate-900 transition-colors font-poppins font-semibold whitespace-nowrap cursor-pointer">
              İletişime Geç
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
